import './RdcSection.scss'

import classNames from 'classnames'
import { IzumiMessageKey } from 'izumi/IzumiMessageKey'
import { i18n } from 'lib/i18n'
import React, { useState } from 'react'
import { CountryAndUnit, formatDistance, formatLocalTimeDuration } from 'utils/formatters'

import { getRdcSectionTransportModeTranslation } from '../RoutePathInfo/RoutePathInfo'
import TransportModeFlag from '../TransportModeFlag'
import DelayTime from './DelayTime'
import SectionBreakdownList, {
  LineType,
  SectionBreakdownItem,
} from './SectionBreakdownList/SectionBreakdownList'
import TransportModeIcon from './TransportModeIcon'
import { TransportModeIconMode } from './TransportModeIcon/TransportModeIcon'

export interface RdcSectionProps {
  transport: RdcSectionTransport
  duration: number
  delay?: number
  distance: number
  departure: RdcSectionDeparture
  breakdownItems: SectionBreakdownItem[]
  isCollapsible?: boolean
  className?: string
  countryAndUnit: CountryAndUnit
}

export interface RdcSectionDeparture {
  time: Date
  place?: string
  platform?: string
}

export interface RdcSectionTransport {
  color: string
  headsign?: string
  mode: RdcSectionTransportMode
  name?: string
  textColor: string
}

export enum RdcSectionTransportMode {
  Car = 'car',
  Drive = 'route.transport.mode.drive',
  ParkedCar = 'parking',
  Train = 'route.transport.mode.train',
  Underground = 'route.transport.mode.subway',
  Pedestrian = 'route.transport.mode.walk',
  Tram = 'route.transport.mode.tram',
  Bike = 'route.transport.mode.bike',
  Motorcycle = 'motorcycle',
  Bus = 'route.transport.mode.bus',
  Scooter = 'scooter',
  Ferry = 'route.transport.mode.ferry',
  Unknown = 'unknown',
}

export default function RdcSection({
  breakdownItems,
  delay,
  departure,
  distance,
  duration,
  transport,
  isCollapsible,
  className,
  countryAndUnit,
}: RdcSectionProps): JSX.Element {
  const [toggle, setToggle] = useState(false)
  const isPublicTransport =
    transport.mode === RdcSectionTransportMode.Tram ||
    transport.mode === RdcSectionTransportMode.Train ||
    transport.mode === RdcSectionTransportMode.Bus ||
    transport.mode === RdcSectionTransportMode.Underground ||
    transport.mode === RdcSectionTransportMode.Ferry
  const defaultTitle =
    transport.mode === RdcSectionTransportMode.Pedestrian
      ? i18n(RdcSectionTransportMode.Pedestrian)
      : transport.mode === RdcSectionTransportMode.Bike
      ? i18n(RdcSectionTransportMode.Bike)
      : transport.mode === RdcSectionTransportMode.Ferry
      ? i18n(RdcSectionTransportMode.Ferry)
      : i18n(RdcSectionTransportMode.Drive)
  const ptTitle = i18n('route.instruction.title', [i18n(transport.mode as IzumiMessageKey)])
  const platformInfo = i18n('route.instruction.platform', [departure.platform as string])
  const title = !isPublicTransport
    ? defaultTitle.charAt(0).toLocaleUpperCase() + defaultTitle.slice(1)
    : ptTitle
  const iconMode = fromRdcSectionTransportModeToTransportModeIconMode(transport.mode)
  const subtitle =
    isPublicTransport && departure.place !== undefined
      ? departure.place
      : formatLocalTimeDuration(duration)
  const breakdownListTitle = isPublicTransport
    ? `${i18n('route.instruction.collapsible.title', [breakdownItems.length])}`
    : formatDistance(distance, countryAndUnit)
  const breakdownListDuration = isPublicTransport ? duration : undefined
  const breakdownLineOptions = isPublicTransport
    ? { color: transport.color, type: LineType.BulletPoint }
    : transport.mode === RdcSectionTransportMode.Pedestrian
    ? { color: transport.color, type: LineType.Dotted }
    : { color: transport.color, type: LineType.Solid }
  const borderType = 'solid'
  const isCollapsed = isCollapsible !== undefined && isCollapsible ? toggle : undefined
  const hasPlatform = departure.platform !== undefined && departure.platform !== ''
  const handleToggle =
    isCollapsible !== undefined && isCollapsible
      ? (toggle: boolean) => setToggle(toggle)
      : undefined
  const transportMode = getRdcSectionTransportModeTranslation(transport.mode)

  return (
    <div
      className={classNames('RdcSection', className)}
      style={
        {
          '--border-color-type': borderType,
          '--border-color-var': transport.color,
        } as React.CSSProperties
      }
    >
      <div className="RdcSection__headline">
        <TransportModeIcon
          className="RdcSection__icon"
          mode={iconMode}
          color={transport.color}
        ></TransportModeIcon>
        <div className="RdcSection__header">
          <div className="RdcSection__header-column-1">
            <span className="RdcSection__title">{title}</span>
            <DelayTime
              className={classNames({ 'RdcSection__time--bold': isPublicTransport })}
              time={departure.time}
              duration={duration}
              delay={delay}
            />
          </div>
          <div className="RdcSection__header-column-2">
            <span className="RdcSection__subtitle">{subtitle}</span>
          </div>
        </div>
      </div>
      {hasPlatform && <div className="RdcSection__platform">{platformInfo}</div>}
      {isPublicTransport && (
        <div className="RdcSection__transport">
          <TransportModeFlag color={transport.textColor} bgColor={transport.color} icon={iconMode}>
            {transport.name ?? transportMode}
          </TransportModeFlag>
          <span className="RdcSection__headsign">{transport.headsign}</span>
        </div>
      )}
      <SectionBreakdownList
        breakdownItems={breakdownItems}
        duration={breakdownListDuration}
        title={breakdownListTitle}
        isCollapsed={isCollapsed}
        onToggle={handleToggle}
        delay={delay}
        line={breakdownLineOptions}
        hasPreview={isPublicTransport}
        className="RdcSection__breakdown-list"
      ></SectionBreakdownList>
      <div className="RdcSection__footer">
        <div className="RdcSection__separator" />
      </div>
    </div>
  )
}

// TODO duplicate implementation
function fromRdcSectionTransportModeToTransportModeIconMode(
  mode: RdcSectionTransportMode
): TransportModeIconMode {
  switch (mode) {
    case RdcSectionTransportMode.Bike:
      return TransportModeIconMode.Bike
    case RdcSectionTransportMode.Bus:
      return TransportModeIconMode.Bus
    case RdcSectionTransportMode.Car:
      return TransportModeIconMode.Car
    case RdcSectionTransportMode.Ferry:
      return TransportModeIconMode.Ferry
    case RdcSectionTransportMode.Motorcycle:
      return TransportModeIconMode.Motorcycle
    case RdcSectionTransportMode.ParkedCar:
      return TransportModeIconMode.ParkedCar
    case RdcSectionTransportMode.Pedestrian:
      return TransportModeIconMode.Pedestrian
    case RdcSectionTransportMode.Scooter:
      return TransportModeIconMode.Scooter
    case RdcSectionTransportMode.Train:
      return TransportModeIconMode.Train
    case RdcSectionTransportMode.Tram:
      return TransportModeIconMode.Tram
    case RdcSectionTransportMode.Underground:
      return TransportModeIconMode.Underground
    default:
      return TransportModeIconMode.Unknown
  }
}
